/**
 * CaraLoom · global axios hardening
 * ─────────────────────────────────
 *
 * Single source of truth for axios behavior across the React app. Imported
 * ONCE at app startup (see `index.js`); installs interceptors on the
 * default axios instance so every one of the ~89 existing axios call
 * sites inherits the new behavior with zero migration.
 *
 * What this gives us:
 *
 *   1. **Default 45s timeout** — long enough for Vertex AI / Whisper STT
 *      calls (≤55s gateway), short enough that an upstream stall surfaces
 *      to the user as a toast instead of an infinite spinner.
 *
 *   2. **Auto-attached `Authorization` header** — every request reads the
 *      live `localStorage.token` and attaches `Bearer <token>` if not
 *      already set. Removes the boilerplate `headers: { Authorization }`
 *      pattern from new code (existing call sites that DO set it still
 *      work — explicit headers win).
 *
 *   3. **401 auto-recovery** — when an authenticated call returns 401
 *      (expired/revoked token), we clear local session and route the user
 *      to `/auth?returnTo=<current_path>` so they don't lose context.
 *      Anonymous 401s (e.g., wrong password on the login form itself) are
 *      passed through untouched so the form can show the inline error.
 *
 *   4. **Friendly network-error toast** — unreachable backend or DNS fail
 *      shows a single toast instead of multiple stacking ones. Deduped
 *      by a 5-second window so a burst of failures doesn't spam.
 *
 *   5. **Per-request opt-outs** — set `config._silent = true` to suppress
 *      the toast for a specific call (e.g., background polls that we
 *      know may fail), or `config._skipAuthRedirect = true` to keep the
 *      user on-page after a 401 (auth-flow internal calls).
 */
import axios from 'axios';
import { toast } from 'sonner';

// 45s default — long enough for Vertex AI but bounded.
axios.defaults.timeout = 45_000;

// Auto-attach Authorization header from localStorage on every request,
// unless the call site already supplied one (explicit always wins).
axios.interceptors.request.use((config) => {
  try {
    if (!config.headers || !config.headers.Authorization) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_) {
    /* localStorage may be disabled in private browsing — request proceeds without token */
  }
  return config;
});

// Toast deduplication — same error message within 5s = no extra toast.
let _lastToastMsg = null;
let _lastToastAt = 0;
const _toastDedupe = (msg) => {
  const now = Date.now();
  if (msg === _lastToastMsg && now - _lastToastAt < 5000) return;
  _lastToastMsg = msg;
  _lastToastAt = now;
  toast.error(msg);
};

// Response interceptor — 401 recovery + friendly network-error toast.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error?.config || {};
    const status = error?.response?.status;
    const silent = config._silent === true;
    const skipAuthRedirect = config._skipAuthRedirect === true;

    // 401 with a stored token → session expired or revoked.
    // Clear token + route to /auth, preserving the current path so the
    // login flow can return the user to where they were. Anonymous 401s
    // (no token in storage) are pass-through — the form/page handles it.
    if (status === 401 && !skipAuthRedirect) {
      let hadToken = false;
      try { hadToken = !!localStorage.getItem('token'); } catch (_) { /* private mode */ }
      if (hadToken) {
        try { localStorage.removeItem('token'); } catch (_) { /* noop */ }
        const here = typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : '/';
        // Don't loop the redirect if we're already on /auth.
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
          // Soft toast so the user knows what happened, then route.
          if (!silent) _toastDedupe('Your session expired — please sign in again.');
          window.location.href = `/auth?returnTo=${encodeURIComponent(here)}`;
        }
      }
    } else if (!silent && (
      error?.code === 'ECONNABORTED' ||
      error?.code === 'ERR_NETWORK' ||
      error?.message === 'Network Error' ||
      (typeof status === 'number' && status >= 500)
    )) {
      // Network error, timeout, or 5xx → friendly toast.
      const msg = error?.code === 'ECONNABORTED'
        ? 'That took too long — please try again.'
        : (status >= 500
            ? 'The server hit a snag — please try again in a moment.'
            : 'Network hiccup — please check your connection and try again.');
      _toastDedupe(msg);
    }

    return Promise.reject(error);
  }
);

export default axios;
